import React, { useState, useEffect } from 'react';
import { useParams, generatePath } from 'react-router-dom';
import InputRange from 'react-input-range';
import { Button, PlainButton, LoadMoreButton } from 'molecules';
import { Label, Link, Icon } from 'atoms';
import { GameCard } from 'organisms';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  amountRangeFromGames,
  filterGamesByIds,
  filterGamesByGroup,
  filterFavoritesGames,
  filterRecentGames,
  filterGamesByQuery,
  filterGamesByRange,
  metadataHasFilter,
  nudgeGames,
  sortGamesByDate,
  sortGamesByTitle,
  sortGamesByPopularity,
  sortGamesByJackpot
} from 'app/shared/store/helpers/game';
import { matchMedia } from 'foundation/utilities/breakpoint';
import { formatThousandSeparator } from 'foundation/Helpers/numbers';
import { pushGaEventOnPageRedirect, pushGaEvent } from '../../../foundation/tracking/tracking';

const CategoryOverview = (props) => {
  const games = useSelector(state => state.games.games);
  const currentUser = useSelector(state => state.currentUser.currentUser);
  const [filteredGames, setFilteredGames] = useState([]);
  const [reducedGames, setReducedGames] = useState([]);
  const [pageOffset, setPageOffset] = useState(0);
  const [moreAvailable, setMoreAvailable] = useState(true);
  const [gamesAvailable, setGamesAvailable] = useState(0);
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [price, setPrice] = useState({ min: 0, max: 0 });
  const [maximum, setMaximum] = useState(5);
  const [minimum, setMinimum] = useState(100);
  const [excludeFilter, setExcludeFilter] = useState('');
  const filterPrefix = props.path + '/';
  let { category } = useParams();
  let labelValue = null;
  const [sort, setSort] = useState(props.sorts[0]?.sortItems[0]);
  const quickFilters = props.quickFilters[0]?.quickFilterItems ?? [];

  if (!category && quickFilters.length > 0) {
    category = quickFilters[0].filter;
  }

  const toggle = (stateSetter, active, deactivate) => {
    stateSetter(active);
    if (deactivate) {
      deactivate(false);
    }
    if (active) {
      document.body.classList.add('sub-menu-open');
    } else {
      document.body.classList.remove('sub-menu-open');
    }
  };

  const addQuery = (key, value, filterOrSort) => {
    let pathname = props.location.pathname;
    let searchParams = new URLSearchParams(props.location.search);
    searchParams.set(key, value);
    labelValue = value;
    props.history.replace({
      pathname: pathname,
      search: searchParams.toString()
    });

    if (filterOrSort === 'sortering') {
      category = 'used';
    }
    else {
      filterOrSort = 'filter';
    }

    pushGaEvent(filterOrSort, category ? category : 'Alle', value);
  };

  const removeQuery = (key) => {
    let pathname = props.location.pathname;
    let searchParams = new URLSearchParams(props.location.search);
    searchParams.delete(key);
    props.history.replace({
      pathname: pathname,
      search: searchParams.toString()
    });
  };

  const getQuery = (key) => {
    const searchParams = new URLSearchParams(props.location.search);
    return searchParams.get(key) ?? null;
  };

  const clearFilter = (persist) => {
    let pathname = props.location.pathname;
    let searchParams = new URLSearchParams();
    persist.forEach((key) => {
      const persistValue = getQuery(key);
      if (persistValue) {
        searchParams.set(key, persistValue);
      }
    });
    props.history.replace({
      pathname: pathname,
      search: searchParams.toString()
    });
  };

  const clamp = (value, min, max) => {
    return {
      min: Math.min(Math.max(value.min, min), max),
      max: Math.min(Math.max(value.max, min), max),
    };
  };

  const updateInputRangeLabel = (values) => {
    const min = document.querySelector('.input-range__label--min .input-range__label-container');
    if (min) min.innerHTML = values.min + ' kr.';

    const max = document.querySelector('.input-range__label--max .input-range__label-container');
    if (max) max.innerHTML = values.max + ' kr.';
  };

  const nextPage = (minimum) => {
    let properAmount = 2; //Baseline is mobile
    if (matchMedia('sm')) {
      properAmount = 3;
    }
    if (matchMedia('ml')) {
      properAmount = 4;
    }
    if (matchMedia('xl')) {
      properAmount = 5;
    }
    const modulus = minimum % properAmount;
    return minimum + (modulus > 0 ? properAmount - modulus : 0);
  };

  const handleGames = (filterGames) => {
    let filteredGames = filterGames;

    if (props.nudgeGames?.length > 0) {
      filteredGames = nudgeGames(filterGames, games, props.nudgeGames);
    }

    setMoreAvailable(nextPage(pageOffset + props.pageSize) < filteredGames.length);
    setGamesAvailable(filteredGames.length);
    setFilteredGames(filteredGames);
    setReducedGames(filteredGames.slice(0,nextPage(pageOffset + props.pageSize)));
  };

  const loadMore = () => {
    setPageOffset(prevValue => {
      return prevValue + props.pageSize;
    });
    pushGaEvent('navigation', 'indlaes_flere', window.location.pathname);
  };

  const sortGamesByMethod = (sort, allGames) => {
    switch (sort) {
    case 'popular':
      allGames = sortGamesByPopularity(allGames);
      break;
    case 'new':
      allGames = sortGamesByDate(allGames);
      break;
    case 'alphabetical':
      allGames = sortGamesByTitle(allGames);
      break;
    case 'jackpot':
      allGames = sortGamesByJackpot(allGames);
      break;
    default:
      break;
    }
    return allGames;
  };

  const sortValueFromKey = (key) => {
    return props.sorts[0].sortItems.find((sortItem) =>  sortItem.filter === key);
  };

  const handleGamesUsingFilters = () => {
    let allGames = games;
    if (props.showQuickFilter || props.prefilter) {
      const filterObj = quickFilters.find(filter => filter.value === category);
      if (category && filterObj && filterObj.filter) {
        const filterLabel = decodeURIComponent(filterObj.filter);
        allGames = filterGamesByGroup(allGames, filterLabel);
      } else {
        allGames = filterGamesByGroup(allGames, category);
      }
    }
    if (props.showClassify || props.prefilter) {
      const searchParams = new URLSearchParams(props.location.search);
      let query = {};
      let range = {};
      let sortValue;
      for(const pair of searchParams.entries()) {
        const key = pair[0];
        const value = pair[1];
        if (key === 'amount') {
          range = price;
        } else if (key === 'sort') {
          setSort(sortValueFromKey(value));
          sortValue = value;
          continue;
        } else {
          query[key] = value.split('|');
        }
      }

      allGames = filterGamesByQuery(allGames, query);
      const { min, max } = amountRangeFromGames(allGames);
      setMinimum(min);
      setMaximum(max);
      if (Object.keys(range).length) {
        allGames = filterGamesByRange(allGames, range);
        updateInputRangeLabel(range);
      } else {
        const newRange = { min: min, max: max };
        setPrice(newRange);
        updateInputRangeLabel(newRange);
      }
      if (sortValue) {
        allGames = sortGamesByMethod(sortValue, allGames);
      } else {
        allGames = sortGamesByMethod(sort.filter, allGames);
      }

    }
    handleGames(allGames);
  };

  useEffect(() => {
    if (props.type === 'favorites') {
      handleGames(filterFavoritesGames(games));
    }
    if (props.type === 'select' && props.selectedGames?.length > 0) {
      handleGames(filterGamesByIds(games, props.selectedGames));
    }
    if (props.type === 'new') {
      handleGames(sortGamesByDate(games));
    }
    if (props.type === 'jackpot') {
      handleGames(sortGamesByJackpot(games));
    }
    if (props.type === 'latest') {
      handleGames(filterRecentGames(games));
    }
    if (props.type === 'popular') {
      handleGames(sortGamesByPopularity(games, true));
    }
    if (props.type === 'all') {
      handleGamesUsingFilters();
    }
  },[games, pageOffset]);

  useEffect(() => {
    props.filters.forEach((filter) =>{
      if (filter.type === 'Range') {
        const { maximum, minimum } = filter.filterItems[0];
        setPrice({ min: minimum ?? 0, max: maximum ?? 1000 });
      }
      if (filter.type === 'Text') {
        const hasDefault = filter.filterItems.find((filterItem) => {
          return filterItem.value == 'Alle';
        });
        if (!hasDefault) {
          filter.filterItems.splice(0, 0, { value: 'Alle', filter: '' });
        }
      }
    });
    const hasDefault = quickFilters.find((filter) => {
      return filter.value == 'Alle';
    });
    if (!hasDefault) {
      quickFilters.splice(0,0, { value: 'Alle', filter: '' });
    }
    if (props.prefilter) {
      const queryStart = props.prefilter.indexOf('?');
      let query = props.prefilter;
      if (queryStart > 0) {
        category = query.slice(0,queryStart);
        query = query.slice(queryStart);
        props.history.push({
          pathname: generatePath(props.path + (category ? '/:category': ''), { category: category }),
          search: query.slice(1)
        });
      } else {
        props.history.push({
          pathname: props.path,
          search: query.slice(1)
        });
      }
    }
  }, []);


  const excludeFilterHandler = () => {
    if (category) {
      quickFilters.forEach(element => {
        if (element.value === category) {
          setExcludeFilter(element.excludeFilter);
          return;
        }
      });
    } else {
      setExcludeFilter('');
    }
  };

  const setRangeAmountFromQuery = () => {
    const amount = getQuery('amount')?.split('|');
    const minValue = amount && amount[0];
    const maxValue = amount && amount[1];

    if (amount) {
      setPrice({ min: Number.parseInt(minValue ?? 0, 10), max: Number.parseInt(maxValue ?? 5000, 10) });
      updateInputRangeLabel({ min: minValue, max: maxValue });
    }
  };

  const emptyCards = () => {
    const emptyCardNeeded = nextPage(reducedGames.length) - reducedGames.length;
    let cards = [];

    for (let i = 0; i < emptyCardNeeded; i++) {
      cards.push(
        { index: i }
      );
    }
    return cards;
  };

  useEffect(() => {
    setRangeAmountFromQuery();
    if (props.type === 'all') {
      handleGamesUsingFilters();
    }

    excludeFilterHandler();
  }, [props.location]);

  const track = (url) => {
    pushGaEventOnPageRedirect('navigation','se_hele_vores_spiludvalg_her', window.location.pathname);
    window.location.href = url;
  };

  return (
    <div className='h-category'>
      {
        props.showQuickFilter && <div className="h-category__quick-filter">
          {
            quickFilters && quickFilters.map((filter, index) => {
              const isSelected = category === filter.value || (index === 0 && !category);
              return <Link onClick={ () => {
                pushGaEvent('filter', filter.value, labelValue ? labelValue : 'Alle');
              }} to={filterPrefix + (index > 0 ? filter.value : '') + (sort?.filter && sort.filter !== 'popular' ? '?sort=' + sort.filter : '')}
              className={`a-filter-label ${isSelected ? 'a-filter-label--selected' : ''}`}
              key={index}>{filter.value}</Link>;
            })}
        </div>
      }
      { props.showClassify && <div className="h-category__classify">
        <div className="h-category__sort-wrapper">
          <PlainButton onClick={() => {
            toggle(setSortOpen, true, setFilterOpen);
          }}>
            <span>Sortering</span>
            <Icon className={'h-category__classify-icon'} iconId="#icon-chevron" />
          </PlainButton>
          <div className={`h-category__sort ${sortOpen ? 'h-category__sort--toggled' : ''}`}>
            <span className={'h-category__sort-title'}>Sortering</span>
            <PlainButton className={'a-plain-button--close'} onClick={() => {toggle(setSortOpen, false);}}>
              <span>Luk</span>
              <Icon className={'m-icon-button__asset'} iconId="#icon-close" />
            </PlainButton>
            <>
              {props.sorts && props.sorts.map((sort, sortIndex) => {
                const values = sort.sortItems && sort.sortItems.map((value, index) => {
                  if (excludeFilter === value.filter) return;
                  const isSelected = getQuery(sort.identifier) === value.filter || (sortIndex === 0 && index === 0 && !getQuery(sort.identifier));
                  return <Link onClick={() => {addQuery(sort.identifier, value.filter, 'sortering');} }
                    className={`a-filter-label ${isSelected ? 'a-filter-label--selected' : ''}`}
                    key={index}>{value.value}</Link>;
                });
                return <div key={sortIndex}>
                  <div className={'h-category__sort-menu'}>{values}</div>
                </div>;
              })}
            </>
          </div>
          {
            sortOpen && <div className='h-category__backdrop' onClick={() => {toggle(setSortOpen,false);}}></div>
          }
        </div>
        {gamesAvailable > 0 && <div className='h-category__games-available'>{formatThousandSeparator(gamesAvailable)} Spil</div>}
        <div className="h-category__filter-wrapper">
          <PlainButton onClick={() => {
            toggle(setFilterOpen, true, setSortOpen);
          }}>
            <span>Filtre</span>
            <Icon className={'h-category__classify-icon'} iconId="#icon-chevron" />
          </PlainButton>
          <div className={`h-category__filter ${filterOpen ? 'h-category__filter--toggled' : ''}`}>
            <PlainButton className={'a-plain-button--reset'} onClick={() => {clearFilter(['sort']);}}>
              <span>Nulstil filtre</span>
            </PlainButton>
            <span className={'h-category__filter-title'}>Filtre</span>
            <PlainButton className={'a-plain-button--close'} onClick={() => {toggle(setFilterOpen, false);}}>
              <span>Luk</span>
              <Icon className={'m-icon-button__asset'} iconId="#icon-close" />
            </PlainButton>
            <>
              {props.filters && props.filters.map((filter, filterIndex) => {
                const isActive = getQuery(filter.identifier);
                let values;
                if (filter.type === 'Range') {
                  values = <div className={'h-category__filter-range'}><InputRange
                    step={5}
                    formatLabel={value => `${value} kr.`}
                    maxValue={maximum}
                    minValue={minimum}
                    value={price}
                    onChange={value => {
                      const newRange = clamp(value,minimum,maximum);
                      setPrice(newRange);
                      updateInputRangeLabel(newRange);
                      addQuery(filter.identifier, [value.min,value.max].join('|'));
                    }} />

                  </div>;

                } else {
                  values = filter.filterItems && filter.filterItems.map((value, index) => {
                    const metadataAvailable = metadataHasFilter(filteredGames, filter.identifier, value.filter);
                    const isAvailable = metadataAvailable || index === 0;
                    return <Link onClick={() => {
                      if (index === 0) {
                        // Reset filter to all
                        removeQuery(filter.identifier);
                      } else {
                        if (isAvailable) {
                          addQuery(filter.identifier, value.filter);
                        }
                      }
                    }}
                    className={`a-filter-label
                      ${value.filter && getQuery(filter.identifier)?.indexOf(value.filter) > -1 ? 'a-filter-label--selected' : ''}
                      ${!isAvailable ? 'a-filter-label--disabled' : ''}
                      ${!isActive && index === 0 ? 'a-filter-label--selected' : ''}`}
                    key={index}>{value.value}</Link>;
                  });
                }

                return <div key={filterIndex}>
                  <Label htmlFor={filter.identifier+'-filter-label'}>{filter.title}</Label>
                  <div className={`h-category__filter-menu h-category__filter-menu--${filter.type}`}>{values}</div>
                </div>;
              })}
            </>
            <div className={'h-category__filter-apply'}>
              <Button onClick={() => {
                toggle(setFilterOpen, false);}
              }>{`Vis spil (${formatThousandSeparator(gamesAvailable)})`}</Button>
            </div>
          </div>
          {
            filterOpen && <div className='h-category__backdrop' onClick={() => {toggle(setFilterOpen,false);}}></div>
          }
        </div>
      </div>
      }
      { reducedGames.length > 0 &&
        <div className="h-category__games">
          {reducedGames && reducedGames.map((game, index) => <GameCard key={index}
            canFavorize={currentUser.isAuthenticated}
            {...game}
          />)}
          {
            emptyCards().map((ignore, index) => {
              return <GameCard key={index} isHidden={true}/>;
            })
          }
        </div>
      }
      {moreAvailable ? <LoadMoreButton onClick={loadMore} /> : props.moreGamesLink &&
        <Link target={props.moreGamesLink.Target} onClick={() => track(props.moreGamesLink.Url)} className={'h-category__more-games'}>
          <span>{props.moreGamesLink.Text}</span>
          <Icon className={'a-icon--link'} iconId='#icon-chevron' />
        </Link>}
    </div>
  );
};

CategoryOverview.defaultProps = {
  showQuickFilter: false,
  showClassify: false,
};

CategoryOverview.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
  }),
  showQuickFilter: PropTypes.bool,
  showClassify: PropTypes.bool,
  prefilter: PropTypes.string,
  title: PropTypes.string,
  selectedGames: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string
  })),
  path: PropTypes.string,
  type: PropTypes.string,
  pageSize: PropTypes.number,
  nudgeGames: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    position: PropTypes.number
  })),
  moreGamesLink: PropTypes.shape({
    Url: PropTypes.string,
    Text: PropTypes.string,
    Target: PropTypes.string
  }),
  quickFilters: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    quickFilterItems: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      filter: PropTypes.string
    }))
  })),
  sorts: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    sortItems: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      filter: PropTypes.string,
    }))
  })),
  filters: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    type: PropTypes.string,
    filterItems: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      filter: PropTypes.string,
      minimum: PropTypes.number,
      maximum: PropTypes.number
    }))
  }))
};

export default CategoryOverview;
